/**
 * TAT breach notification API client.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiRequest } from '../utils/apiClient';

export interface TatNotificationMetadata {
  accession_number?: string;
  study_instance_uid?: string | null;
  patient_name?: string | null;
  patient_uhid?: string | null;
  modality?: string | null;
  test_name?: string | null;
  priority?: string;
  tat_hours?: number;
  elapsed_minutes?: number;
  pacs_created_time?: string;
  report_status?: string | null;
}

export interface TatNotification {
  id: number;
  accession_number: string;
  study_instance_uid: string | null;
  alert_type: string;
  facility_id: number;
  recipient_user_id: number;
  recipient_role: 'Admin' | 'Radiologist';
  message: string;
  metadata: TatNotificationMetadata | null;
  is_read: boolean;
  breach_detected_at: string;
  created_at: string;
  updated_at: string;
}

interface UnreadCountResponse {
  success: boolean;
  data: { count: number };
}

interface ListResponse {
  success: boolean;
  data: TatNotification[];
}

export async function getTatUnreadCount(): Promise<number> {
  const response = await apiGet<UnreadCountResponse>(API_ENDPOINTS.TAT_NOTIFICATIONS.UNREAD_COUNT, {
    requireAuth: true,
  });
  return response.data?.data?.count ?? 0;
}

export async function listTatNotifications(
  limit = 50,
  options: { unreadOnly?: boolean } = {}
): Promise<TatNotification[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (options.unreadOnly) {
    params.set('unread_only', 'true');
  }
  const url = `${API_ENDPOINTS.TAT_NOTIFICATIONS.LIST}?${params.toString()}`;
  const response = await apiGet<ListResponse>(url, { requireAuth: true });
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export function formatTatNotificationContent(notification: TatNotification): {
  patientName: string;
  patientId: string;
  modality: string;
  testName: string;
} {
  const meta = notification.metadata || {};
  return {
    patientName: meta.patient_name || 'Unknown',
    patientId: meta.patient_uhid || 'N/A',
    modality: meta.modality || 'N/A',
    testName: meta.test_name || 'N/A',
  };
}

function buildTatWorklistHref(): string {
  return '/?risstudypagination=1';
}

function buildTatViewerHref(studyUid: string): string {
  return `/viewer?StudyInstanceUIDs=${encodeURIComponent(studyUid)}`;
}

/**
 * Admin alerts open the imaging worklist (no accession filter).
 * Radiologist alerts open the study viewer when a UID is available.
 */
export function buildTatNotificationHref(notification: TatNotification): string {
  const meta = notification.metadata || {};
  const studyUid = notification.study_instance_uid || meta.study_instance_uid;

  if (notification.recipient_role === 'Radiologist' && studyUid) {
    return buildTatViewerHref(studyUid);
  }

  return buildTatWorklistHref();
}

export async function markTatNotificationRead(id: number): Promise<void> {
  await apiRequest(API_ENDPOINTS.TAT_NOTIFICATIONS.MARK_READ(id), {
    method: 'PATCH',
    requireAuth: true,
  });
}

export async function markAllTatNotificationsRead(): Promise<void> {
  await apiRequest(API_ENDPOINTS.TAT_NOTIFICATIONS.MARK_ALL_READ, {
    method: 'PATCH',
    requireAuth: true,
  });
}
